/*
 * Module som innehåller timeouts som ska triggra events när specifika conditions uppfylls.
 * Ett exempel är onDayChange som kollar om dagen när användaren gick in i systemet gått över till en annan. Om den
 *  gjort det så kommer alla funktioner som skickats in som lyssnare att anropas
 */

import cloneDeep from "lodash/cloneDeep";

enum EventName {
    DayChange = "onDayChange",
}

type Trigger = [key: EventName, listener: Map<string, () => void>];

type State = Record<Trigger[0], Trigger[1]> & {
    // timeout id kopplad till varje typ av event
    timeoutIds: {
        [key in EventName]: number | null;
    };
};

type Commit = (
    action: string,
    payload: unknown,
    options?: {
        root?: boolean;
    }
) => void;

type Dispatch = (eventName: EventName) => void;

type StartTriggerProps = {
    time: number; // Mängden millisekunder för timeout
    eventName: EventName; // Namn på eventet
    condition: () => boolean; // Funktion som returnerar boolean. Avgör när timeoiuten ska dispatcha event
    dispatch: Dispatch;
    commit: Commit;
};

let today = new Date();

/**
 * Starta en timeout som agerar baserat på inskickad condition
 */
const startTrigger = ({ time, eventName, dispatch, commit, condition }: StartTriggerProps) => {
    return setTimeout(async () => {
        if (condition()) {
            dispatch(eventName);
        }

        commit("updateTrigger", {
            eventName,
            timeoutId: startTrigger({
                time,
                eventName,
                dispatch,
                condition,
                commit,
            }),
        });
    }, time);
};

const getDefaultState: () => State = () => {
    return {
        onDayChange: new Map(),
        timeoutIds: {
            onDayChange: null,
        },
    };
};

export default {
    namespaced: true,
    state: getDefaultState(),
    mutations: {
        reset(state: State) {
            Object.assign(state, getDefaultState());
        },
        /**
         * Lägg till en ny timeout (via dess id) i samlingen
         */
        addTrigger: (
            state: State,
            {
                eventName,
                timeoutId,
            }: {
                eventName: EventName;
                timeoutId: number;
            }
        ) => {
            if (state.timeoutIds[eventName]) {
                clearTimeout(state.timeoutIds[eventName] as number);
            }

            state.timeoutIds = {
                ...cloneDeep(state.timeoutIds),
                [eventName]: timeoutId,
            };
        },
        updateTrigger: (
            state: State,
            {
                eventName,
                timeoutId,
            }: {
                eventName: EventName;
                timeoutId: number;
            }
        ) => {
            state.timeoutIds = {
                ...cloneDeep(state.timeoutIds),
                [eventName]: timeoutId,
            };
        },
        removeTrigger: (
            state: State,
            {
                eventName,
            }: {
                eventName: EventName;
            }
        ) => {
            if (state.timeoutIds[eventName]) {
                clearTimeout(state.timeoutIds[eventName] as number);
            }

            state.timeoutIds = {
                ...cloneDeep(state.timeoutIds),
                [eventName]: null,
            };
        },
        /**
         * Lägg till funktion som kommer anropas när triggern aktiveras
         */
        addListener: (
            state: State,
            {
                eventName,
                listener,
                id,
            }: {
                eventName: EventName;
                listener: () => void;
                id: string;
            }
        ) => {
            const modifiedListeners = new Map([...state[eventName].entries()]);

            modifiedListeners.set(id, listener);

            state[eventName] = modifiedListeners;
        },
        removeListener: (
            state: State,
            {
                eventName,
                id,
            }: {
                eventName: EventName;
                id: string;
            }
        ) => {
            const modifiedListeners = new Map([...state[eventName].entries()]);

            modifiedListeners.delete(id);

            state[eventName] = modifiedListeners;
        },
    },
    getters: {},
    actions: {
        /**
         * Anropas när dagen gått över till en annan
         */
        onDayChange: ({ state }: { state: State }) => {
            [...state[EventName.DayChange].values()].forEach((func) => {
                func();
            });
        },
        /**
         * Startar koll för när dagen går över till en annan
         */
        startOnDayChange: ({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) => {
            const timeoutId = startTrigger({
                dispatch,
                commit,
                // Funktionen som kommer anropas varje 10000 millisekunder för att kolla om eventet ska triggras
                condition: () => {
                    const now = new Date();

                    if (now.getDay() > today.getDay() || now.getMonth() > today.getMonth()) {
                        today = now;

                        return true;
                    }

                    return false;
                },
                time: 10000,
                eventName: EventName.DayChange,
            });

            commit("addTrigger", {
                timeoutId,
                eventName: EventName.DayChange,
            });
        },
        stopOnDayChange: async ({ commit }: { commit: Commit }) => {
            commit("removeTrigger", {
                eventName: EventName.DayChange,
            });
        },
    },
};
